
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { CheckIcon } from "lucide-react";

type PlaceholderFormProps = {
  placeholders: string[];
  onValuesChange: (values: Record<string, string>) => void;
  initialValues: Record<string, string>;
  displayNames?: Record<string, string>;
  className?: string;
};

export function PlaceholderForm({ 
  placeholders,
  onValuesChange,
  initialValues,
  displayNames = {},
  className = ""
}: PlaceholderFormProps) {
  // Count filled placeholders for progress indicator
  const placeholderCount = placeholders.length;
  const filledPlaceholders = Object.values(initialValues).filter(value => value && value.trim() !== '').length;
  const progressPercentage = placeholderCount > 0 ? Math.round((filledPlaceholders / placeholderCount) * 100) : 0;

  const handleInputChange = (placeholder: string, value: string) => {
    onValuesChange({
      ...initialValues,
      [placeholder]: value
    });
  };

  const getDisplayName = (fieldId: string): string => {
    return displayNames[fieldId] || fieldId;
  };

  return (
    <div className={`flex flex-col h-full ${className}`}>
      <div>
        <h3 className="font-medium text-sm">Document Fields</h3>
        <Badge variant={progressPercentage === 100 ? "success" : "outline"} className="text-xs">
          {progressPercentage}% Complete
        </Badge>
      </div>
      
      <ScrollArea className="flex-1 pr-4">
        <div className="space-y-3">
          {placeholders?.map((placeholder: string) => {
            const isFilled = initialValues[placeholder] && initialValues[placeholder].trim() !== '';
            const displayName = getDisplayName(placeholder);
            
            return (
              <div key={placeholder} className="space-y-1">
                <div className="flex justify-between items-center">
                  <Label className="text-xs font-medium">
                    {displayName}
                  </Label>
                  {isFilled && (
                    <CheckIcon className="h-3 w-3 text-green-500" />
                  )}
                </div>
                <Input
                  value={initialValues[placeholder] || ''}
                  onChange={(e) => handleInputChange(placeholder, e.target.value)}
                  placeholder={`Enter ${displayName}`}
                  className="h-8 text-sm"
                />
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
}
